
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { NavItem } from './types';

interface NavItemProps {
  item: NavItem;
  activePage: string;
  setActivePage: (page: string) => void;
  toggleSection?: (section: 'publication' | 'analytics' | 'online-store' | 'social-media') => void;
}

const NavItemComponent = ({ 
  item, 
  activePage, 
  setActivePage, 
  toggleSection 
}: NavItemProps) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (item.isExpandable && toggleSection) {
      toggleSection(item.id as 'publication' | 'analytics' | 'online-store' | 'social-media');
    } else {
      setActivePage(item.id);
      navigate('/dashboard');
    }
  };

  return (
    <li key={item.id}>
      {/* Main menu item */}
      <div 
        className={`flex items-center justify-between px-6 py-3 cursor-pointer transition-colors duration-200
          ${activePage === item.id 
            ? 'text-[#8b5cf6] dark:text-[#a78bfa] border-l-4 border-[#8b5cf6] dark:border-[#a78bfa] pl-[1.35rem]' 
            : 'text-gray-700 dark:text-gray-300 border-l-4 border-transparent hover:bg-gray-50 dark:hover:bg-gray-800/40'}`}
        onClick={handleClick}
      >
        <div className="flex items-center">
          {item.icon && React.isValidElement(item.icon) && 
            <span className={`w-5 h-5 ${activePage === item.id ? 'text-[#8b5cf6] dark:text-[#a78bfa]' : 'text-gray-500 dark:text-gray-400'}`}>
              {item.icon}
            </span>
          }
          <span className={`ml-3 ${activePage === item.id ? 'font-medium' : ''}`}>
            {item.label}
            {item.isPremium && item.premiumIcon && React.isValidElement(item.premiumIcon) && (
              <span className="inline-flex items-center">
                {item.premiumIcon}
              </span>
            )}
          </span>
        </div>
        {item.isExpandable && (
          item.isExpanded ? 
          <ChevronUp className="w-4 h-4 text-gray-500 dark:text-gray-400" /> : 
          <ChevronDown className="w-4 h-4 text-gray-500 dark:text-gray-400" />
        )}
      </div>
      
      {/* Sub-menu items */}
      {item.isExpandable && item.isExpanded && item.children && (
        <ul className="ml-11 mt-1 space-y-1 border-l border-gray-200 dark:border-gray-700 pl-4">
          {item.children.map((child) => (
            <li 
              key={child.id}
              className={`py-2.5 cursor-pointer text-sm transition-colors duration-200
                ${activePage === child.id 
                  ? 'text-[#8b5cf6] dark:text-[#a78bfa] font-medium' 
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300'}`}
              onClick={() => {
                setActivePage(child.id);
                navigate('/dashboard');
              }}
            >
              <div className="flex items-center">
                {child.icon && React.isValidElement(child.icon) && 
                  <span className="w-4 h-4 mr-2 text-gray-500 dark:text-gray-400">
                    {child.icon}
                  </span>
                }
                {child.label}
              </div>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
};

export default NavItemComponent;
